import { useRef, useState } from 'react';
import {
  calc, customerStats, dateOf, fDate, fShort, findCustomers, inr, numbersOf, phoneKey, sharedNumber, span, type Customer, type DocRef,
} from '@nandanam/domain';
import { useStore } from '../app/store';
import { Icon } from '../ui/icons';
import { Avatar, Empty, Field, PageHead, Segment, StatusPill } from '../ui/components';
import { pkgLabel } from '../app/selectors';
import { avatarColors } from './Pipeline';
import { DocGrid } from './Record';
import { MAX_UPLOAD_BYTES } from '../app/files';

type F = 'all' | 'repeat' | 'balance' | 'open';

export function Customers() {
  const S = useStore();
  const [q, setQ] = useState('');
  const f = (S.route.params.filter as F) || 'all';
  const rows = S.state.customers.map(c => ({ c, st: customerStats(S.state.records, c.id, S.now) }));
  const pool = q.trim() ? new Set(findCustomers(S.state.customers, q).map(c => c.id)) : null;
  const match = (x: (typeof rows)[number]) => (!pool || pool.has(x.c.id)) && (f === 'all' || (f === 'repeat' && x.st.bookings > 1) || (f === 'balance' && x.st.outstanding > 0) || (f === 'open' && x.st.open > 0));
  const list = rows.filter(match).sort((a, b) => (b.st.last ?? b.c.since).localeCompare(a.st.last ?? a.c.since));
  const repeat = rows.filter(x => x.st.bookings > 1).length;
  const count = (k: F) => rows.filter(x => (!pool || pool.has(x.c.id)) && (k === 'all' || (k === 'repeat' && x.st.bookings > 1) || (k === 'balance' && x.st.outstanding > 0) || (k === 'open' && x.st.open > 0))).length;
  return (
    <div className="page">
      <PageHead icon="users" title="Customers" sub={`${rows.length} customers · ${repeat} repeat`}>
        <label className="search"><Icon name="search" size={14} /><input placeholder="Search name or any number" value={q} onChange={e => setQ(e.target.value)} /></label>
        <button className="btn primary" onClick={() => S.open('new', {})}><Icon name="plus" size={15} />New enquiry</button>
      </PageHead>
      <Segment value={f} options={([['all', 'All'], ['repeat', 'Repeat'], ['balance', 'With balance'], ['open', 'Open enquiry']] as [F, string][]).map(([v, label]) => ({ v, label, count: count(v) }))} onChange={v => S.nav('customers', { filter: v })} />
      <div className="card" style={{ padding: 8 }}>
        {!list.length ? <Empty /> : (
          <div className="table-wrap">
            <table className="t cards">
              <thead><tr><th>Customer</th><th>Phone</th><th className="r">Bookings</th><th>Last visit</th><th className="r">Lifetime value</th><th className="r">Outstanding</th><th className="r">Open</th></tr></thead>
              <tbody>{list.map(({ c, st }) => {
                const [bg, fg] = avatarColors(c.name);
                return (
                  <tr key={c.id} className="click" onClick={() => S.nav('customer', { id: c.id })}>
                    <td><div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Avatar name={c.name} bg={bg} fg={fg} /><div style={{ minWidth: 0 }}><div style={{ fontWeight: 600 }}>{c.name} {st.bookings > 1 && <span className="pill st-good" style={{ fontSize: 10.5 }}>Repeat</span>}</div>{c.notes && <div className="muted ellipsis" style={{ fontSize: 12, maxWidth: 320 }}>{c.notes.split(/(?<=\.)\s/)[0]}</div>}</div></div></td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>{c.phone}</td>
                    <td className="r num" data-l="Bookings">{st.bookings}</td>
                    <td className="m-hide">{st.last ? fDate(st.last, { dow: false, year: true }) : <span className="muted">—</span>}</td>
                    <td className="r num" data-l="Value">{inr(st.value)}</td>
                    <td className="r num m-end" data-l="Due" style={{ color: st.outstanding ? 'var(--warn)' : undefined }}>{st.outstanding ? inr(st.outstanding) : '—'}</td>
                    <td className="r num m-hide">{st.open || '—'}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomerDetail() {
  const S = useStore(), id = S.route.params.id ?? '';
  const c = S.state.customers.find(x => x.id === id);
  if (!c) return <div className="page"><Empty title="Customer not found">It may not have synced to this device yet.</Empty></div>;
  return <CustomerView key={c.id} c={c} />;
}

function CustomerView({ c }: { c: Customer }) {
  const S = useStore(), st = customerStats(S.state.records, c.id, S.now);
  const [form, setForm] = useState({ name: c.name, phone: c.phone, whatsapp: c.whatsapp, alt: c.alt, email: c.email, notes: c.notes });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const dirty = (Object.keys(form) as (keyof typeof form)[]).some(k => form[k] !== c[k]);
  const shared = [...new Set(numbersOf(c).flatMap(n => sharedNumber(S.state.customers, n, c.id)))];
  const recs = [...st.records].sort((a, b) => span(b).start.localeCompare(span(a).start));
  const comms = recs.flatMap(r => r.comms.map(m => ({ ...m, r }))).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 8);
  const bookingDocs = recs.flatMap(r => r.docs);
  const [bg, fg] = avatarColors(c.name);
  const wa = c.whatsapp || c.phone;

  const save = async () => {
    setErr(null);
    if (!form.name.trim()) return setErr('Enter the name');
    if (phoneKey(form.phone).length !== 10) return setErr('Enter a 10-digit mobile number');
    setSaving(true);
    const x = await S.act('customer.update', `customer:${c.id}`, { id: c.id, patch: { ...form, name: form.name.trim() } }, 'Customer updated');
    setSaving(false);
    if (!x.ok) setErr(x.error!);
  };
  const addDoc = async (f?: File) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) return S.toast('That file is over 20 MB');
    const doc: DocRef = await S.files.put(f, { id: S.newId(), folder: `documents/customers/${c.id}`, cat: 'ID proof', date: dateOf(S.now) });
    const x = await S.act('customer.update', `customer:${c.id}`, { id: c.id, patch: { docs: [...c.docs, doc] } }, 'Document added');
    if (!x.ok) S.toast(x.error!);
  };
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="page">
      <button className="crumb" onClick={() => S.nav('customers')}><Icon name="left" size={14} />Customers</button>
      <header className="page-head">
        <Avatar name={c.name} size={52} bg={bg} fg={fg} />
        <div style={{ minWidth: 0 }}>
          <h1 className="page-title">{c.name}</h1>
          <div className="page-sub">{c.phone}{c.whatsapp && c.whatsapp !== c.phone ? ` · WhatsApp ${c.whatsapp}` : ''}{c.alt ? ` · ${c.alt}` : ''} · customer since {fDate(c.since, { dow: false, year: true })}</div>
        </div>
        <div className="head-actions">
          <a className="btn" href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}><Icon name="phone" size={15} />Call</a>
          <a className="btn" href={`https://wa.me/${phoneKey(wa).length === 10 ? '91' + phoneKey(wa) : wa.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"><Icon name="message" size={15} />WhatsApp</a>
          <button className="btn" onClick={() => S.open('new', { cust: c.id, kind: 'stay' })}><Icon name="bed" size={15} />New stay</button>
          <button className="btn primary" onClick={() => S.open('new', { cust: c.id, kind: 'event' })}><Icon name="cake" size={15} />New event enquiry</button>
        </div>
      </header>
      <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {[['Bookings', String(st.bookings)], ['Lifetime value', inr(st.value)], ['Outstanding', st.outstanding ? inr(st.outstanding) : '—'], ['Last visit', st.last ? fDate(st.last, { dow: false, year: true }) : '—'], ['Open enquiries', String(st.open)]].map(([k, v]) => <div key={k} className="kpi"><div className="muted" style={{ fontSize: 12.5 }}>{k}</div><div className="val num">{v}</div></div>)}
      </div>
      <div className="two-col">
        <div className="col">
          <section className="card">
            <h3 className="card-sub" style={{ marginBottom: 10 }}>History</h3>
            {!recs.length ? <div className="muted">No enquiries or bookings yet.</div> : (
              <div className="table-wrap"><table className="t">
                <thead><tr><th>Dates</th><th>Requirement</th><th>Status</th><th className="r">Total</th><th className="r">Balance</th></tr></thead>
                <tbody>{recs.map(r => { const k = calc(r), sp = span(r); return (
                  <tr key={r.id} className="click" onClick={() => S.nav('booking', { id: r.id })}>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>{fShort(sp.start, sp.end)}<div className="muted" style={{ fontSize: 11.5 }}>{S.num(r)}</div></td>
                    <td>{r.type}<div className="muted" style={{ fontSize: 12 }}>{pkgLabel(r)} · {r.guests} guests</div></td>
                    <td><StatusPill phase={S.phase(r)} /></td>
                    <td className="r num">{inr(k.total)}</td>
                    <td className="r num" style={{ color: k.balance > 0 ? 'var(--warn)' : undefined }}>{k.balance > 0 ? inr(k.balance) : '—'}</td>
                  </tr>); })}</tbody>
              </table></div>
            )}
          </section>
          <section className="card">
            <h3 className="card-sub" style={{ marginBottom: 10 }}>Messages</h3>
            {!comms.length ? <div className="muted" style={{ fontSize: 13.5 }}>No WhatsApp messages logged yet.</div> : comms.map(m => (
              <button key={m.id} className="msg-row" onClick={() => S.open('message', { id: m.r.id, tpl: m.key })}>
                <Icon name="message" size={15} /><span style={{ flex: 1, textAlign: 'left' }}>{S.state.settings.templates[m.key]?.label ?? m.key} <span className="muted">· {m.action === 'copied' ? 'copied' : m.action === 'picture' ? 'picture shared' : 'opened in WhatsApp'} · {S.num(m.r)}</span></span><span className="muted" style={{ fontSize: 12 }}>{fDate(m.date)}</span>
              </button>
            ))}
          </section>
          <section className="card">
            <div className="section-head"><h3 className="card-sub">Documents</h3>
              <button className="btn small" disabled={!S.canEdit} onClick={() => file.current?.click()}><Icon name="upload" size={14} />Add ID proof</button>
              <input ref={file} type="file" hidden accept="image/*,application/pdf" onChange={e => { void addDoc(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            {c.docs.length + bookingDocs.length === 0 ? <div className="muted" style={{ fontSize: 13.5 }}>No documents.</div> : <DocGrid docs={[...c.docs, ...bookingDocs]} />}
          </section>
        </div>
        <div className="col">
          <section className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 className="card-sub">Contact</h3>
            <Field label="Name"><input className="input" value={form.name} onChange={set('name')} disabled={!S.canEdit} /></Field>
            <Field label="Mobile"><input className="input" inputMode="tel" value={form.phone} onChange={set('phone')} disabled={!S.canEdit} /></Field>
            <Field label="WhatsApp"><input className="input" inputMode="tel" value={form.whatsapp} onChange={set('whatsapp')} disabled={!S.canEdit} /></Field>
            <Field label="Alternate number"><input className="input" inputMode="tel" value={form.alt} onChange={set('alt')} disabled={!S.canEdit} /></Field>
            <Field label="Email"><input className="input" type="email" value={form.email} onChange={set('email')} disabled={!S.canEdit} /></Field>
            <Field label="Notes"><textarea className="textarea" rows={4} value={form.notes} onChange={set('notes')} disabled={!S.canEdit} /></Field>
            {shared.length > 0 && <div className="info-box">Shares a number with {shared.map(x => x.name).join(', ')}. Family numbers are shared on purpose — records are never merged by phone alone.</div>}
            {err && <div className="error-box"><b>Not saved.</b> {err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {dirty && <button className="btn" onClick={() => setForm({ name: c.name, phone: c.phone, whatsapp: c.whatsapp, alt: c.alt, email: c.email, notes: c.notes })}>Undo changes</button>}
              <button className="btn primary" disabled={!dirty || saving || !S.canEdit} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
